import React, { useState, useEffect, useMemo } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Typography,
  Divider,
  Box,
  IconButton,
  Table as MUITable,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  TableFooter,
  Grid,
  TablePagination,
  Checkbox,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";

const TableTitle = styled(Typography)({
  padding: 10,
  fontWeight: 600,
  color: "#333",
  backgroundColor: "#D3F0E0",
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: " #000",
  padding: 10,
  textAlign: "left",
  "&:hover": {
    background: "#F2F9F5",
  },
}));

const TableHeaderCell = styled(StyledTableCell)({
  color: "#666666",
});

const TableRowStyled = styled(TableRow)(({ theme }) => ({
  cursor: "pointer",
}));

const TableFooterStyled = styled(TableFooter)({
  color: "#666666",
});

const StyledIconButton = styled(IconButton)({
  cursor: "pointer",
});

const Table = ({
  header,
  preHeaders,
  headers,
  aligns = [],
  headerSpans = [],
  headerActions = [],
  colSpans = [],
  items,
  itemFormatters,
  rowHighlighted = null,
  rowHighlightedAlt = null,
  rowDisabled = null,
  rowLocked = null,
  withPagination = false,
  withCheckbox = false,
  page = 0,
  pageSize,
  count,
  size,
  rowsPerPageOptions = [10, 20, 50],
  onChangeRowsPerPage,
  onChangePage,
  onDoubleClick,
  onDelete = null,
  fetching = null,
  error = null,
  onRowHover = () => {},
  onRowLeave = () => {},
  onSelectedChanged = (selected) => {},
  allowSelection = (item) => {
    return true;
  },
  selectedItems = [],
  ...props
}) => {
  const [selection, setSelection] = useState({});
  // const [hoveredRowId, setHoveredRowId] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (props.withSelection) {
      setSelection(_atom(props.selection || []));
    }
  }, []);

  const _atom = (a) =>
    !!a &&
    a.reduce((m, i) => {
      m[itemIdentifier(i)] = i;
      return m;
    }, {});

  const itemIdentifier = (i) => {
    if (!!props.itemIdentifier) {
      return props.itemIdentifier(i);
    } else {
      return i.uuid;
    }
  };

  const isSelected = (i) =>
    !!props.withSelection && !!selection[itemIdentifier(i)];

  const select = (i, e) => {
    if (e.type === "click") {
      if (!props.withSelection) return;
      let s = selection;
      let id = itemIdentifier(i);
      if (!!s[id]) {
        delete s[id];
      } else if (props.withSelection === "multiple") {
        s[id] = i;
      } else {
        s = { [id]: i };
      }
      setSelection(s);
      !!props.onChangeSelection &&
        props.onChangeSelection(Object.values(selection));
    }
  };

  const headerAction = (a) => (
    <Box flexGrow={1}>
      <Box display="flex" justifyContent="flex-end">
        {a()}
      </Box>
    </Box>
  );

  let localHeaders = [...(headers || [])];
  let localPreHeaders = !!preHeaders ? [...preHeaders] : null;
  let localItemFormatters = [...itemFormatters];
  var i = !!headers && headers.length;

  if (!!onDelete) {
    if (localPreHeaders) localPreHeaders.push("");
    localHeaders.push("");
    localItemFormatters.push((i, idx) => (
      <StyledIconButton onClick={(e) => onDelete(idx)}>
        <DeleteIcon />
      </StyledIconButton>
    ));
  }

  const rowsPerPage = pageSize || rowsPerPageOptions[0];

  const handleSelectAllClick = (event) => {
    let selection = [];
    if (event.target.checked) {
      selection = items.filter(allowSelection).map((n) => itemIdentifier(n));
    }

    onSelectedChanged(selection);
  };

  const handleClickItem = (event, row) => {
    const rowId = itemIdentifier(row);
    let selection = [];
    if (event.target.checked) {
      selection = [...selectedItems, rowId];
    } else {
      selection = selectedItems.filter((el) => el !== rowId);
    }

    onSelectedChanged(selection);
  };

  const isChecked = (row) => selectedItems.indexOf(itemIdentifier(row)) !== -1;

  const numSelected = useMemo(() => selectedItems.length, [selectedItems]);
  const rowCount = useMemo(
    () => items ? items.filter(allowSelection).length : 0,
    [items, allowSelection]
  );

  return (
    <Box position="relative" overflow="auto">
      {header && (
        <React.Fragment>
          <TableTitle>{header}</TableTitle>
          <Divider />
        </React.Fragment>
      )}
      <MUITable>
        {!!localPreHeaders && localPreHeaders.length > 0 && (
          <TableHead style={{ backgroundColor: "#F2F9F5" }}>
            <TableRow>
              {localPreHeaders.map((h, idx) => {
                if (headerSpans.length > idx && !headerSpans[idx]) return null;
                return (
                  <TableHeaderCell
                    colSpan={headerSpans.length > idx ? headerSpans[idx] : 1}
                    key={`preh-${idx}`}
                  >
                    {!!h && h}
                  </TableHeaderCell>
                );
              })}
            </TableRow>
          </TableHead>
        )}
        {!!localHeaders && localHeaders.length > 0 && (
          <TableHead style={{ backgroundColor: "#F2F9F5" }}>
            <TableRow>
              {withCheckbox && (
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    onChange={handleSelectAllClick}
                    indeterminate={numSelected > 0 && numSelected < rowCount}
                    checked={rowCount > 0 && numSelected === rowCount}
                    inputProps={{
                      "aria-label": "select all desserts",
                    }}
                  />
                </TableCell>
              )}
              {localHeaders.map((h, idx) => {
                if (headerSpans.length > idx && !headerSpans[idx]) return null;
                return (
                  <TableHeaderCell
                    colSpan={headerSpans.length > idx ? headerSpans[idx] : 1}
                    key={`h-${idx}`}
                  >
                    {!!h && (
                      <Box
                        style={{
                          width: "100%",
                          cursor:
                            headerActions.length > idx &&
                            !!headerActions[idx][0]
                              ? "pointer"
                              : "",
                        }}
                        onClick={
                          headerActions.length > idx
                            ? headerActions[idx][0]
                            : null
                        }
                        display="flex"
                        alignItems="center"
                        justifyContent={
                          aligns.length > idx ? aligns[idx] : "left"
                        }
                      >
                        <Box>
                          {typeof h === "function" ? (
                            <Box>{() => h(this.state, this.props)}</Box>
                          ) : (
                            h
                          )}
                        </Box>
                        {headerActions.length > idx
                          ? headerAction(headerActions[idx][1])
                          : null}
                      </Box>
                    )}
                  </TableHeaderCell>
                );
              })}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {items &&
            items.length > 0 &&
            items.map((i, iidx) => (
              <TableRowStyled
                key={iidx}
                selected={isSelected(i)}
                onClick={(e) => select(i, e)}
                onMouseEnter={() => onRowHover(i)}
                onMouseLeave={() => onRowLeave(i)}
                onContextMenu={
                  onDoubleClick ? () => onDoubleClick(i, true) : undefined
                }
                onDoubleClick={
                  onDoubleClick ? () => onDoubleClick(i) : undefined
                }
                // className={clsx(
                //   !!rowLocked && rowLocked(i) ? classes.tableLockedRow : null,
                //   !!rowHighlighted && rowHighlighted(i)
                //     ? classes.tableHighlightedRow
                //     : null,
                //   !!rowHighlightedAlt && rowHighlightedAlt(i)
                //     ? classes.tableHighlightedAltRow
                //     : null,
                //   !!rowDisabled && rowDisabled(i)
                //     ? classes.tableDisabledRow
                //     : null,
                //   !!onDoubleClick && classes.clickable
                // )}
              >
                {withCheckbox && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      onChange={(e) => handleClickItem(e, i)}
                      checked={allowSelection(i) ? isChecked(i) : undefined}
                      disabled={!allowSelection(i)}
                      inputProps={{
                        "aria-label": "select all desserts",
                      }}
                    />
                  </TableCell>
                )}
                {localItemFormatters &&
                  localItemFormatters.map((f, fidx) => {
                    if (colSpans.length > fidx && !colSpans[fidx]) return null;
                    return (
                      <StyledTableCell
                        colSpan={colSpans.length > fidx ? colSpans[fidx] : 1}
                        key={`v-${iidx}-${fidx}`}
                      >
                        {f(i, iidx)}
                      </StyledTableCell>
                    );
                  })}
              </TableRowStyled>
            ))}
        </TableBody>
        {!!withPagination && !!count && (
          <TableFooterStyled>
            <TableRow>
              <TablePagination
                labelRowsPerPage={t("rowsPerPage")}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} ${t("of")} ${count}`
                }
                count={count}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={rowsPerPageOptions}
                onRowsPerPageChange={(e) => onChangeRowsPerPage(e.target.value)}
                onPageChange={onChangePage}
              />
            </TableRow>
          </TableFooterStyled>
        )}
      </MUITable>
      {(fetching || error) && (
        <Grid container justifyContent="center" alignItems="center">
          {/* Progress or Error component */}
        </Grid>
      )}
    </Box>
  );
};

export default Table;
